import { refreshToken } from '@sunrise-ui/api-client';
import {
  getStoredCredentials,
  parseAuthTokenForCredentials,
  useAuthStore,
} from '@sunrise-ui/primitives';
import { useEffect } from 'react';

/**
 * Hook that initializes the user session by hydrating tokens from AsyncStorage
 * and attempting to refresh the session if a refresh token exists.
 */
export function useInitializeSession() {
  useEffect(() => {
    const { setIsAuthenticated, setAuthData } = useAuthStore.getState();
    const initializeSession = async () => {
      // Step 1: Hydrate tokens from AsyncStorage
      const { authToken: storedAuthToken, refreshToken: storedRefreshToken } =
        await getStoredCredentials();

      // Case 1: Both tokens exist and auth token is not expired - optimistically authenticate
      if (storedAuthToken && storedRefreshToken) {
        const { isExpired, userId } =
          parseAuthTokenForCredentials(storedAuthToken);

        if (!isExpired && userId) {
          // Axios interceptor will refresh the token if it there is any issue
          await setAuthData({
            authToken: storedAuthToken,
            refreshToken: storedRefreshToken,
            currentUserId: userId,
          });
          return;
        }
      }

      // Case 2: Auth token expired or missing but refresh token exists - get new auth token
      if (storedRefreshToken) {
        await refreshToken(storedRefreshToken);
        return;
      }

      // Case 3: No tokens - unauthenticated
      setIsAuthenticated(false);
    };

    initializeSession().catch(() => {
      setIsAuthenticated(false);
    });
  }, []);
}
